import { useMutation, useQuery } from '@apollo/client/react';
import {
  Dispatch,
  SetStateAction,
  useEffect,
  useEffectEvent,
  useState,
} from 'react';

import { useAppointmentContext } from '@providers/AppointmentProvider/AppointmentContext';
import { EventTypes, emitter } from '@services/emitter';
import { AppointmentStateItem } from '@src/types/appointment';
import {
  appointmentState,
  appointmentSyncState,
} from '@utils/queries/appointment';

interface StateData<S> {
  state: S;
}

export const useSyncState = <S = undefined>(
  key: string,
  initialState: S | (() => S)
): [S, Dispatch<SetStateAction<S>>] => {
  const [state, setState] = useState<S>(initialState);
  const { appointment, hasControl, canUpdateState, isSyncedAppointment } =
    useAppointmentContext();
  const [syncState] = useMutation(appointmentSyncState);

  const { data, refetch } = useQuery(appointmentState, {
    skip: !appointment || !isSyncedAppointment,
    fetchPolicy: 'network-only',
    variables: {
      id: appointment?.id ?? '',
      key,
    },
  });

  const handleConnectionRestored = useEffectEvent(() => {
    if (hasControl) {
      if (!appointment) return;
      syncState({
        variables: {
          id: appointment.id,
          key,
          data: {
            state,
          },
        },
      });
    } else {
      refetch();
    }
  });

  useEffect(() => {
    if (!isSyncedAppointment) return;

    emitter.on(EventTypes.RemoteConnectionRestored, handleConnectionRestored);
    return () => {
      emitter.off(
        EventTypes.RemoteConnectionRestored,
        handleConnectionRestored
      );
    };
  }, [isSyncedAppointment]);

  useEffect(() => {
    const remoteState = data?.appointmentState?.state as S | undefined;
    if (typeof remoteState === 'undefined' || hasControl) return;

    setState(remoteState);
  }, [data, isSyncedAppointment, hasControl]);

  useEffect(() => {
    const handleRemoteChanged = (value: AppointmentStateItem) => {
      if (!hasControl && value.key === key) {
        setState((value.data as StateData<S>).state);
      }
    };
    emitter.on(EventTypes.RemoteStateChanged, handleRemoteChanged);
    return () => {
      emitter.off(EventTypes.RemoteStateChanged, handleRemoteChanged);
    };
  }, [hasControl, isSyncedAppointment, canUpdateState, key]);

  const update: Dispatch<SetStateAction<S>> = async (
    data: S | ((prevState: S) => S)
  ) => {
    if (!isSyncedAppointment)
      return setState((prev) =>
        typeof data === 'function' ? (data as (p: S) => S)(prev) : (data as S)
      );

    if (!canUpdateState || !appointment) return;

    setState((prev) => {
      const resolved =
        typeof data === 'function' ? (data as (p: S) => S)(prev) : (data as S);

      syncState({
        variables: {
          id: appointment.id,
          key,
          data: {
            state: resolved,
          },
        },
      });
      return resolved;
    });
  };

  return [state, update];
};
